import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Truck, MessageCircle, ShieldCheck } from "lucide-react";
import PageShell from "@/components/PageShell";
import { useCart } from "@/shop/CartContext";
import { inr } from "@/shop/products";
import { API } from "@/shop/api";
import { track, pixelCookies } from "@/lib/pixel";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#0A0A0A] px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#7ED321] focus:ring-1 focus:ring-[#7ED321]";

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", pincode: "" });
  const [loading, setLoading] = useState(false);
  const [payError, setPayError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/shop/config`).then((r) => setConfig(r.data)).catch(() => setConfig({ payments_enabled: false, cod_enabled: true, shipping_fee: 49, free_shipping_above: 500 }));
    if (searchParams.get("payment") === "failed") {
      setPayError("Payment failed or was cancelled — no money was captured. Your cart is intact, retry anytime.");
    }
    if (items.length > 0) {
      track("InitiateCheckout", {
        content_ids: items.map((i) => i.product.sku),
        content_type: "product",
        num_items: items.reduce((a, i) => a + i.qty, 0),
        value: subtotal,
        currency: "INR",
      });
    }
  }, []);

  if (items.length === 0) {
    return (
      <PageShell>
        <section className="px-5 py-24 text-center" data-testid="checkout-empty">
          <h1 className="font-display text-4xl uppercase text-white">Your cart is empty</h1>
          <a href="/shop" className="mt-8 inline-flex rounded-full bg-[#7ED321] px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#0A0A0A]">
            Back to Shop
          </a>
        </section>
      </PageShell>
    );
  }

  const shipping = subtotal >= (config?.free_shipping_above ?? 500) ? 0 : config?.shipping_fee ?? 49;
  const total = subtotal + shipping;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const valid = form.name.trim() && form.phone.trim().length >= 10 && form.address.trim().length >= 5 && form.pincode.trim().length >= 5;

  const createOrder = (method) =>
    axios.post(`${API}/orders`, {
      customer: form,
      items: items.map((i) => ({ slug: i.slug, qty: i.qty })),
      payment_method: method,
      ...pixelCookies(),
    });

  const whatsappFallback = () => {
    const lines = [
      "Hi Zentra! I'd like to order:",
      ...items.map((i) => `• ${i.qty} × ${i.product.title} — ${inr(i.product.price * i.qty)}`),
      `Shipping: ${shipping === 0 ? "Free" : inr(shipping)}`,
      `Total: ${inr(total)}`,
      `Name: ${form.name} | Phone: ${form.phone}`,
      `Address: ${form.address}, ${form.pincode}`,
    ];
    window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  const payWithPayu = async () => {
    setLoading(true);
    setPayError("");
    try {
      const { data: order } = await createOrder("payu");
      const { data: payu } = await axios.post(`${API}/orders/${order.order_id}/payu/start`);
      const payForm = document.createElement("form");
      payForm.method = "POST";
      payForm.action = payu.action;
      Object.entries(payu.fields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = String(value);
        payForm.appendChild(input);
      });
      document.body.appendChild(payForm);
      payForm.submit();
    } catch (e) {
      setPayError(e.response?.data?.detail || e.message || "Could not start payment. Please retry.");
      setLoading(false);
    }
  };

  const placeCodOrder = async () => {
    setLoading(true);
    try {
      const { data: order } = await createOrder("cod");
      clear();
      const lines = [
        `Hi Zentra! COD order placed: ${order.order_number}`,
        ...items.map((i) => `• ${i.qty} × ${i.product.title} — ${inr(i.product.price * i.qty)}`),
        `Total: ${inr(order.total)} (pay on delivery)`,
        `Name: ${form.name} | Phone: ${form.phone}`,
        `Address: ${form.address}, ${form.pincode}`,
      ];
      window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
      navigate(`/thank-you/${order.order_id}`);
    } catch (e) {
      toast.error("Could not place the order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <Helmet>
        <title>Checkout | Zentra</title>
      </Helmet>
      <section className="px-5 py-14 lg:px-12" data-testid="checkout-page">
        <div className="mx-auto grid max-w-[1100px] grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <h1 className="font-display text-4xl uppercase leading-[0.95] text-white" data-testid="checkout-heading">Checkout</h1>
            <p className="mt-2 text-sm text-[#A6A66A]">Delivery details for your Zentra order.</p>
            <div className="mt-8 space-y-4">
              <input required value={form.name} onChange={set("name")} placeholder="Full name *" data-testid="checkout-name-input" className={inputCls} />
              <input required value={form.phone} onChange={set("phone")} placeholder="Phone (10 digits) *" inputMode="tel" data-testid="checkout-phone-input" className={inputCls} />
              <input value={form.email} onChange={set("email")} placeholder="Email (for order updates)" type="email" data-testid="checkout-email-input" className={inputCls} />
              <textarea required value={form.address} onChange={set("address")} placeholder="Full delivery address *" rows={3} data-testid="checkout-address-input" className={`${inputCls} resize-none`} />
              <input required value={form.pincode} onChange={set("pincode")} placeholder="PIN code *" inputMode="numeric" data-testid="checkout-pincode-input" className={inputCls} />
            </div>
          </div>

          <div>
            <div className="rounded-2xl border border-white/10 bg-[#121212] p-6" data-testid="checkout-summary">
              <h2 className="font-display text-2xl uppercase tracking-wide text-white">Order Summary</h2>
              <ul className="mt-5 space-y-3">
                {items.map((i) => (
                  <li key={i.slug} className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-white/80">
                      {i.product.title} <span className="text-white/40">× {i.qty}</span>
                    </span>
                    <span className="shrink-0 font-semibold text-white">{inr(i.product.price * i.qty)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 space-y-2 border-t border-white/10 pt-4 text-sm">
                <div className="flex justify-between text-white/60">
                  <span>Subtotal</span>
                  <span>{inr(subtotal)}</span>
                </div>
                <div className="flex justify-between text-white/60">
                  <span>Shipping</span>
                  <span data-testid="checkout-shipping">{shipping === 0 ? "Free" : inr(shipping)}</span>
                </div>
                <div className="flex justify-between border-t border-white/10 pt-3">
                  <span className="text-xs uppercase tracking-[0.18em] text-white/50">Total</span>
                  <span className="font-display text-3xl text-[#7ED321]" data-testid="checkout-total">{inr(total)}</span>
                </div>
              </div>
              <p className="mt-3 text-[11px] text-white/40">
                Free shipping on orders of {inr(config?.free_shipping_above ?? 500)} and above.
              </p>
            </div>

            <div className="mt-6 space-y-3">
              {config?.payments_enabled ? (
                <button
                  onClick={payWithPayu}
                  disabled={!valid || loading}
                  data-testid="pay-payu-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#7ED321] py-4 text-sm font-bold uppercase tracking-[0.12em] text-[#0A0A0A] transition-colors hover:bg-[#8FE331] disabled:opacity-50"
                >
                  <CreditCard className="h-4 w-4" />
                  {loading ? "Opening PayU…" : `Pay ${inr(total)} with PayU`}
                </button>
              ) : (
                <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 text-sm text-[#A6A66A]" data-testid="payments-not-configured">
                  Online payment isn't switched on yet. Order on WhatsApp instead — we confirm
                  payment (UPI / COD) and delivery personally.
                  <button
                    onClick={whatsappFallback}
                    disabled={!valid}
                    data-testid="checkout-whatsapp-fallback"
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#2be374] disabled:opacity-50"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Order on WhatsApp
                  </button>
                </div>
              )}

              {config?.cod_enabled && (
                <button
                  onClick={placeCodOrder}
                  disabled={!valid || loading}
                  data-testid="cod-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-white/20 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:border-[#7ED321] hover:text-[#7ED321] disabled:opacity-50"
                >
                  <Truck className="h-4 w-4" />
                  Cash on Delivery
                </button>
              )}

              {payError && (
                <p className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="payment-error">
                  {payError}
                </p>
              )}

              <p className="flex items-center justify-center gap-2 pt-2 text-[11px] uppercase tracking-[0.15em] text-white/35">
                <ShieldCheck className="h-3.5 w-3.5 text-[#7ED321]" />
                Secure payment via PayU · UPI, cards, netbanking, wallets
              </p>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Checkout;
